import { type RefObject, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { Editor, Extensions } from '@tiptap/react';
import { useTextBuildingBlocks } from 'hooks/useTextBuildingBlocks';
import { placeholderExtensions } from './placeholders/placeholderNodes';
import {
  TemplateSuggestion,
  type TemplateSuggestionBridge,
} from './placeholders/templateSuggestion';
import { usePlaceholderNavigation } from './placeholders/usePlaceholderNavigation';
import { useSelectionListResolution } from './placeholders/useSelectionListResolution';
import { useTextBuildingBlockAutocomplete } from './placeholders/useTextBuildingBlockAutocomplete';
import { SelectionListSelect } from './SelectionListSelect';
import { TextBuildingBlockDropdown } from './TextBuildingBlockDropdown';
import { useDropdownPlacement } from './useDropdownPlacement';

interface NavigationHandle {
  selectList: (pos: number) => void;
  advance: (fromPos: number) => boolean;
  retreat: (fromPos: number) => boolean;
}

type KeyHandler = (event: KeyboardEvent) => boolean;

export interface TextBuildingBlockSetup {
  extensions: Extensions;
  onKeyDown: KeyHandler;
  navigationRef: RefObject<NavigationHandle | null>;
  suggestionBridgeRef: RefObject<TemplateSuggestionBridge | null>;
  activationPosRef: RefObject<number | null>;
  keyHandlerRef: RefObject<KeyHandler | null>;
}

// The editor needs these extensions at creation, but the hooks that drive them need the editor, so
// the first phase hands out extensions wired to refs that useTextBuildingBlockEditing fills later.
export function useTextBuildingBlockSetup(): TextBuildingBlockSetup {
  const navigationRef = useRef<NavigationHandle | null>(null);
  const suggestionBridgeRef = useRef<TemplateSuggestionBridge | null>(null);
  const activationPosRef = useRef<number | null>(null);
  const keyHandlerRef = useRef<KeyHandler | null>(null);

  const extensions = useMemo(
    () => [
      ...placeholderExtensions({
        onActivateList: (pos) => navigationRef.current?.selectList(pos),
        onAdvance: (fromPos) => navigationRef.current?.advance(fromPos) ?? false,
        onRetreat: (fromPos) => navigationRef.current?.retreat(fromPos) ?? false,
        consumeActivation: (pos) => {
          if (activationPosRef.current !== pos) return false;
          activationPosRef.current = null;
          return true;
        },
      }),
      TemplateSuggestion.configure({ getBridge: () => suggestionBridgeRef.current }),
    ],
    []
  );
  const onKeyDown = useCallback<KeyHandler>((event) => keyHandlerRef.current?.(event) ?? false, []);

  return {
    extensions,
    onKeyDown,
    navigationRef,
    suggestionBridgeRef,
    activationPosRef,
    keyHandlerRef,
  };
}

export function useTextBuildingBlockEditing({
  section,
  editor,
  setup,
}: {
  section: string;
  editor: Editor;
  setup: TextBuildingBlockSetup;
}) {
  const { navigationRef, suggestionBridgeRef, activationPosRef, keyHandlerRef } = setup;
  const [container, setContainer] = useState<HTMLDivElement | null>(null);
  const blurTimer = useRef<number | null>(null);
  const [isFocused, setIsFocused] = useState(false);
  const { data, isError } = useTextBuildingBlocks();
  const selectionLists = useMemo(
    () => (data ?? []).flatMap((block) => block.selectionLists),
    [data]
  );

  const navigation = usePlaceholderNavigation({
    editor,
    onActivate: (pos) => {
      activationPosRef.current = pos;
    },
  });
  const resolution = useSelectionListResolution({
    editor,
    activeListPos: navigation.activeListPos,
    selectionLists,
    onResolved: navigation.goToNextFrom,
  });
  const onInserted = useCallback(
    (fromPos: number) => navigation.goToNextFrom(fromPos),
    [navigation]
  );
  const autocomplete = useTextBuildingBlockAutocomplete({
    section,
    editor,
    textBuildingBlocks: data ?? [],
    loadError: isError,
    onInserted,
  });

  keyHandlerRef.current = (event) => {
    if (resolution.isOpen && resolution.onKeyDown(event)) return true;
    return navigation.onKeyDown(event);
  };

  useEffect(() => {
    navigationRef.current = {
      selectList: (pos: number) => editor.chain().focus().setNodeSelection(pos).run(),
      advance: (fromPos: number) => navigation.goToNextFrom(fromPos),
      retreat: (fromPos: number) => navigation.goToPrevFrom(fromPos),
    };
  }, [editor, navigation, navigationRef]);

  useEffect(() => {
    suggestionBridgeRef.current = autocomplete.bridge;
  }, [autocomplete.bridge, suggestionBridgeRef]);

  // Track focus at the container: a slot's <input> blurs the editable but stays inside the field.
  const closeAutocomplete = autocomplete.close;
  const deselectPlaceholder = navigation.deselect;
  useEffect(() => {
    if (!container) return undefined;
    const onFocusIn = () => {
      if (blurTimer.current !== null) window.clearTimeout(blurTimer.current);
      setIsFocused(true);
    };
    const onFocusOut = (event: FocusEvent) => {
      if (container.contains(event.relatedTarget as Node | null)) return;
      setIsFocused(false);
      deselectPlaceholder();
      if (blurTimer.current !== null) window.clearTimeout(blurTimer.current);
      blurTimer.current = window.setTimeout(() => closeAutocomplete(), 0);
    };
    container.addEventListener('focusin', onFocusIn);
    container.addEventListener('focusout', onFocusOut);
    return () => {
      container.removeEventListener('focusin', onFocusIn);
      container.removeEventListener('focusout', onFocusOut);
    };
  }, [container, closeAutocomplete, deselectPlaceholder]);

  const placement = useDropdownPlacement({
    editor,
    container,
    resolutionOpen: resolution.isOpen,
    resolutionAnchorPos: resolution.anchorPos,
    autocompleteOpen: autocomplete.isOpen,
    getAutocompleteAnchorRect: autocomplete.getAnchorRect,
    autocompleteItemCount: autocomplete.items.length,
  });

  useEffect(
    () => () => {
      if (blurTimer.current !== null) window.clearTimeout(blurTimer.current);
    },
    []
  );

  const dropdown =
    isFocused && resolution.isOpen ? (
      <SelectionListSelect
        name={resolution.name}
        options={resolution.options}
        multiSelect={resolution.multiSelect}
        available={resolution.available}
        selected={resolution.selected}
        activeIndex={resolution.activeIndex}
        maxHeight={placement.maxHeight}
        position={placement.resolutionAnchor}
        onOptionClick={resolution.onOptionClick}
        onNoSelection={resolution.onNoSelection}
        onCommit={resolution.onCommit}
        onHover={resolution.onHover}
      />
    ) : isFocused && autocomplete.isOpen ? (
      <TextBuildingBlockDropdown
        items={autocomplete.items}
        activeIndex={autocomplete.activeIndex}
        message={autocomplete.message}
        maxHeight={placement.maxHeight}
        position={placement.autocompleteAnchor}
        onSelect={autocomplete.select}
        onHover={autocomplete.setActiveIndex}
      />
    ) : null;

  return {
    containerRef: setContainer,
    dropdown,
    isToolbarDisabled: navigation.isPlaceholderActive || autocomplete.isOpen,
  };
}
